<script setup>
import PlusIcon from '@iconify-vue/ph/plus';
import { ErrorMessage, Field } from 'vee-validate';
import { ref } from 'vue';

const emit = defineEmits(['update:hours', 'update:minutes']);

const props = defineProps({
    hours: {
        required: true,
    },
    minutes: {
        required: true,
    },
    hoursFieldId: {
        type: String,
        required: true,
    },
    minutesFieldId: {
        type: String,
        required: true,
    },
    hoursFieldName: {
        type: String,
        required: true,
    },
    minutesFieldName: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    required: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const hours = ref(null);

const minutes = ref(null);

function addMinutes(value) {
    let totalInMinutes =
        (props.hours ? parseInt(props.hours) : 0) * 60 +
        (props.minutes ? parseInt(props.minutes) : 0) +
        value;
    let newHours = Math.floor(totalInMinutes / 60);
    let newMinutes = totalInMinutes % 60;

    if (newHours > 0) {
        emit('update:hours', newHours.toString());
        hours.value.handleChange(newHours.toString());
    }

    emit('update:minutes', newMinutes.toString());
    minutes.value.handleChange(newMinutes.toString());
}
</script>

<template>
    <div class="mb-4 flex flex-col">
        <label class="mb-2">
            {{ props.label }}
            <sup v-if="props.required" class="text-red-500">*</sup>
        </label>

        <div class="flex flex-wrap gap-2">
            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(5)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                5min
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(10)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                10min
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(15)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                15min
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(30)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                30min
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(45)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                45min
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(60)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                1h
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(75)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                1h15
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(90)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                1h30
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(105)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                1h45
            </button>

            <button
                type="button"
                class="mb-3 inline-flex items-center self-start rounded-sm bg-gray-200 px-3 py-1 duration-150 hover:bg-gray-300"
                @click="addMinutes(120)"
            >
                <PlusIcon class="mr-2 h-4 w-4" />
                2h
            </button>
        </div>

        <div>
            <div class="flex items-center gap-2">
                <div>
                    <Field
                        :id="props.hoursFieldId"
                        :name="props.hoursFieldName"
                        placeholder="hh"
                        :modelValue="props.hoursModelValue"
                        :value="props.hours"
                        :disabled="props.disabled"
                        @input="$emit('update:hours', $event.target.value)"
                        class="h-[50px] max-w-[4rem] rounded-sm border border-gray-300 px-5 py-3 text-center file:mr-3 placeholder:text-gray-400 hover:file:bg-indigo-950 focus:ring-gray-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        ref="hours"
                    />
                </div>

                <div>:</div>

                <div>
                    <Field
                        :id="props.minutesFieldId"
                        :name="props.minutesFieldName"
                        placeholder="mm"
                        :modelValue="props.minutesModelValue"
                        :value="props.minutes"
                        :disabled="props.disabled"
                        @input="$emit('update:minutes', $event.target.value)"
                        class="h-[50px] max-w-[4rem] rounded-sm border border-gray-300 px-5 py-3 text-center file:mr-3 placeholder:text-gray-400 hover:file:bg-indigo-950 focus:ring-gray-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                        ref="minutes"
                    />
                </div>
            </div>

            <div class="mt-1 flex flex-col gap-1">
                <ErrorMessage
                    :name="props.hoursFieldName"
                    v-slot="{ message }"
                    class="text-sm text-red-500"
                    as="div"
                >
                    hh : {{ message }}
                </ErrorMessage>

                <ErrorMessage
                    :name="props.minutesFieldName"
                    class="mt-1 text-sm text-red-500"
                    v-slot="{ message }"
                    as="div"
                >
                    mm : {{ message }}
                </ErrorMessage>
            </div>
        </div>
    </div>
</template>
